import { ethers, BigNumber } from "ethers";
import { addresses } from "../constants";
import ierc20ABIJson from "../abi/IERC20.json";
import OlympusStakingABIJson from "../abi/OlympusStakingv2.json";
import StakingHelperABIJson from "../abi/StakingHelper.json";

import { clearPendingTxn, fetchPendingTxns, getStakingTypeText } from "./PendingTxnsSlice";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { fetchAccountSuccess, getBalances, loadAccountDetails } from "./AccountSlice";
import { error, info } from "./MessagesSlice";
import { IJsonRPCError } from "./interfaces";
import { segmentUA } from "../helpers/userAnalyticHelpers";
import { IERC20, OlympusStakingv2, StakingHelper } from "src/typechain";
import ReleasePoolABIJson from "../abi/ReleasePool.json";
import ReleasePoolHelperABIJson from "../abi/ReleasePoolHelper.json";
import { t } from "@lingui/macro";
import RouterABIJson from "../abi/RouterContract.json";
import RestakingPoolABIJson from "../abi/restaking/RestakingPool.json";
import FlashRestakingABIJson from "../abi/restaking/FlashRestaking.json";
import logger from "src/utils/logger";
const ierc20ABI = ierc20ABIJson.abi;
const OlympusStakingABI = OlympusStakingABIJson.abi;
const StakingHelperABI = StakingHelperABIJson.abi;
const ReleasePoolABI = ReleasePoolABIJson.abi;
const ReleasePoolHelperABI = ReleasePoolHelperABIJson.abi;
const FlashRestakingABI = FlashRestakingABIJson.abi;
const RouterABI = RouterABIJson.abi;
const RestakingPoolABI = RestakingPoolABIJson.abi;

export const changeApproval = createAsyncThunk(
  "stake/changeApproval",
  async ({ token, provider, address, networkID, restakingPoolType, isFlashRestaking }: any, { dispatch }) => {
    if (!provider) {
      dispatch(error(t`Please connect your wallet!`));
      return;
    }
    logger.info("changeApproval", { token, provider, address, networkID, restakingPoolType, isFlashRestaking });
    const signer = provider;
    const usdtContract = new ethers.Contract(addresses[networkID].USDT_ADDRESS as string, ierc20ABI, signer) as IERC20;
    const sohmContract = new ethers.Contract(addresses[networkID].SOHM_ADDRESS as string, ierc20ABI, signer) as IERC20;
    const ohmContract = new ethers.Contract(addresses[networkID].OHM_ADDRESS as string, ierc20ABI, signer) as IERC20;
    let approveTx;
    const restakingPoolAddress = [
      addresses[networkID].restakingPool360,
      addresses[networkID].restakingPool180,
      addresses[networkID].restakingPool90,
    ][restakingPoolType];
    console.log("restakingPoolAddress", restakingPoolAddress);
    try {
      if (token === "ohm") {
        const estimateGas = await ohmContract.estimateGas.approve(
          addresses[networkID].flashRestaking,
          ethers.utils.parseUnits("1000000", "9").toString(),
        );
        approveTx = await ohmContract.approve(
          addresses[networkID].flashRestaking,
          ethers.utils.parseUnits("1000000", "9").toString(),
          {
            gasLimit: estimateGas.add(ethers.utils.parseUnits("100000", "wei")),
          },
        );
      } else if (token === "USDT") {
        // won't run if stakeAllowance > 0
        const estimateGas = await usdtContract.estimateGas.approve(
          addresses[networkID].flashRestaking,
          ethers.utils.parseUnits("1000000", "18").toString(),
        );

        approveTx = await usdtContract.approve(
          addresses[networkID].flashRestaking,
          ethers.utils.parseUnits("1000000", "18").toString(),
          {
            gasLimit: estimateGas.add(ethers.utils.parseUnits("100000", "wei")),
          },
        );
      } else if (token === "sOhm") {
        if (isFlashRestaking) {
          const estimateGas = await sohmContract.estimateGas.approve(
            addresses[networkID].flashRestaking,
            ethers.utils.parseUnits("1000000", "9").toString(),
          );
          approveTx = await sohmContract.approve(
            addresses[networkID].flashRestaking,
            ethers.utils.parseUnits("1000000", "9").toString(),
            {
              gasLimit: estimateGas.add(ethers.utils.parseUnits("100000", "wei")),
            },
          );
        } else {
          const estimateGas = await sohmContract.estimateGas.approve(
            addresses[networkID].flashRestaking,
            ethers.utils.parseUnits("1000000", "9").toString(),
          );
          approveTx = await sohmContract.approve(
            addresses[networkID].flashRestaking,
            ethers.utils.parseUnits("1000000", "9").toString(),
            {
              gasLimit: estimateGas.add(ethers.utils.parseUnits("100000", "wei")),
            },
          );
        }
      }

      const text = "Approve " + (token === "USDT" ? "Flash_Restaking" : "Restaking");
      const pendingTxnType = isFlashRestaking ? "approve_flashRestaking" : "approve_restaking";
      if (approveTx) {
        dispatch(
          fetchPendingTxns({
            txnHash: approveTx.hash,
            text,
            type: pendingTxnType,
          }),
        );

        await approveTx.wait();
      }
    } catch (e) {
      // dispatch(error((e as IJsonRPCError).message));
      if ((e as any).code == "ACTION_REJECTED") {
        dispatch(error(t`User denied transaction signature.`));
        // dispatch(error((e as any).message));
      } else if (e == "cancel") {
        dispatch(error(t`User denied transaction signature.`));
      } else {
        // dispatch(error((e as any).message));
        dispatch(error((e as any).reason || (e as any).message || (e as any).data || (e as any)));
      }
      return;
    } finally {
      if (approveTx) {
        dispatch(clearPendingTxn(approveTx.hash));
      }
    }

    // go get fresh allowances
    const flashRestakingSohmAllowance = await sohmContract.allowance(address, addresses[networkID].flashRestaking);
    const flashRestakingUsdtAllowance = await usdtContract.allowance(address, addresses[networkID].flashRestaking);
    const flashRestakingOhmAllowance = await ohmContract.allowance(address, addresses[networkID].flashRestaking);
    const restaking90Allowance = await sohmContract.allowance(address, addresses[networkID].flashRestaking);
    const restaking180Allowance = await sohmContract.allowance(address, addresses[networkID].flashRestaking);
    const restaking360Allowance = await sohmContract.allowance(address, addresses[networkID].flashRestaking);
    console.log("approve staking", {
      // restaking: {
      flashRestakeAllowance: Number(ethers.utils.formatUnits(flashRestakingSohmAllowance, 9)),
      restaking90Allowance: Number(ethers.utils.formatUnits(restaking90Allowance, 9)),
      restaking180Allowance: Number(ethers.utils.formatUnits(restaking180Allowance, 9)),
      restaking360Allowance: Number(ethers.utils.formatUnits(restaking360Allowance, 9)),
      // },
    });
    return dispatch(
      fetchAccountSuccess({
        // restaking: {
        flashRestakingSohmAllowance: Number(ethers.utils.formatUnits(flashRestakingSohmAllowance, 9)),
        flashRestakingUsdtAllowance: Number(ethers.utils.formatUnits(flashRestakingUsdtAllowance, 18)),
        flashRestakingOhmAllowance: Number(ethers.utils.formatUnits(flashRestakingOhmAllowance, 9)),
        restaking90Allowance: Number(ethers.utils.formatUnits(restaking90Allowance, 9)),
        restaking180Allowance: Number(ethers.utils.formatUnits(restaking180Allowance, 9)),
        restaking360Allowance: Number(ethers.utils.formatUnits(restaking360Allowance, 9)),
        // },
      }),
    );
  },
);

export const changeRestake = createAsyncThunk(
  "stake/changeRetake",
  async ({ action, value, provider, address, networkID, restakingType }: any, { dispatch }) => {
    if (!provider) {
      dispatch(error(t`Please connect your wallet!`));
      return;
    }

    // const signer = provider.getSigner();
    const signer = provider;
    const flashStaking = new ethers.Contract(
      addresses[networkID].flashRestaking as string,
      FlashRestakingABI,
      signer,
    ) as any;
    const restakingPool = new ethers.Contract(
      [
        addresses[networkID].restakingPool90,
        addresses[networkID].restakingPool180,
        addresses[networkID].restakingPool360,
      ][restakingType] as string,
      RestakingPoolABI,
      signer,
    ) as any;
    console.log(
      "restakingPool",
      // restakingPool,
      restakingType,
      action,
      restakingPool.address,
    );
    let restakeTx;
    const slippage = localStorage.getItem("restakeSlippage") || 0.1;
    const usdtMinAmount = ethers.utils.parseUnits(String(Number(value)), "18");
    try {
      // return;
      if (action === "flashRestaking") {
        const routerContract = new ethers.Contract(
          addresses[networkID].pancakeRouter as string,
          RouterABI,
          signer,
        ) as any;
        const amounts = await routerContract.getAmountsOut(ethers.utils.parseUnits(value, "18"), [
          addresses[networkID].USDT_ADDRESS,
          addresses[networkID].OHM_ADDRESS,
        ]);
        const amount = amounts[amounts.length - 1];

        const amountsOut = ethers.utils.formatUnits(String(Number(amount)), "9");
        console.log("getAmountsOut amountsOut", amountsOut);
        const minOut = ethers.utils.parseUnits(String((Number(amountsOut) * (1 - Number(slippage))).toFixed(4)), "9");
        console.log("getAmountsOut minOut", minOut, { usdtMinAmount, minOut, restakingType });
        const estimateGas = await flashStaking.estimateGas.flashRestake(usdtMinAmount, minOut, restakingType);
        console.log("estimateGas", estimateGas);
        restakeTx = await flashStaking.flashRestake(usdtMinAmount, minOut, restakingType, {
          gasLimit: estimateGas.add(ethers.utils.parseUnits("100000", "wei")),
        });
      } else if (action === "unrestaking_interest") {
        const interest = ethers.utils.parseUnits(String(Number(value)), "9");
        const estimateGas = await restakingPool.estimateGas.withdrawInterest(interest);
        restakeTx = await restakingPool.withdrawInterest(interest, {
          gasLimit: estimateGas.add(ethers.utils.parseUnits("100000", "wei")),
        });
      } else {
        // const estimateGas = await restakingPool.estimateGas.stake(ethers.utils.parseUnits(value, "gwei"));
        // restakeTx = await restakingPool.stake(ethers.utils.parseUnits(value, "gwei"), {
        //   gasLimit: estimateGas.add(ethers.utils.parseUnits("100000", "wei")),
        // });
        const estimateGas = await flashStaking.estimateGas.complexRestake(
          ethers.utils.parseUnits(value, "gwei"),
          restakingType,
        );

        restakeTx = await flashStaking.complexRestake(ethers.utils.parseUnits(value, "gwei"), restakingType, {
          gasLimit: estimateGas.add(ethers.utils.parseUnits("100000", "wei")),
        });
        // restakeTx = await restakingPool["stake(uint256)"](ethers.utils.parseUnits(value, "gwei"));
      }
      const pendingTxnType =
        action === "flashRestaking"
          ? "flashRestaking"
          : action === "unrestaking_interest"
          ? "unrestaking_interest"
          : "restaking";
      dispatch(
        fetchPendingTxns({
          txnHash: restakeTx.hash,
          text: getStakingTypeText(action),
          type: pendingTxnType,
        }),
      );
      await restakeTx.wait();
    } catch (e) {
      console.log("changeRestake error", e);
      const rpcError = e as IJsonRPCError;
      if (rpcError.code === -32603 && rpcError.message.indexOf("ds-math-sub-underflow") >= 0) {
        dispatch(
          error(
            "You may be trying to restake more than your balance! Error code: 32603. Message: ds-math-sub-underflow",
          ),
        );
      } else if ((e as any).code == "ACTION_REJECTED") {
        dispatch(error(t`User denied transaction signature.`));
        // dispatch(error((e as any).message));
      } else if (e == "cancel") {
        dispatch(error(t`User denied transaction signature.`));
      } else {
        // dispatch(error((e as any).message));
        dispatch(error((e as any).reason || (e as any).message || (e as any).data || (e as any)));
      }
      return;
    } finally {
      if (restakeTx) {
        // segmentUA(uaData);
        dispatch(clearPendingTxn(restakeTx.hash));
      }
    }
    dispatch(loadAccountDetails({ address, networkID, provider }));
    dispatch(getBalances({ address, networkID, provider }));
  },
);

// startReleasePrincipal
export const releasePrincipal = createAsyncThunk(
  "stake/releasePrincipal",
  async ({ provider, address, networkID, ids, poolType, isAll }: any, { dispatch }) => {
    if (!provider) {
      dispatch(error(t`Please connect your wallet!`));
      return;
    }

    // const signer = provider.getSigner();
    const signer = provider;
    const restakingPoolContract = new ethers.Contract(
      [
        addresses[networkID].restakingPool360,
        addresses[networkID].restakingPool180,
        addresses[networkID].restakingPool90,
      ][poolType] as string,
      RestakingPoolABI,
      signer,
    ) as any;
    let releasePrincipalTx;

    try {
      if (isAll) {
        const releaseInfoLength = await restakingPoolContract.releaseInfoLength(address);
        console.log("releaseInfoLength", releaseInfoLength);
        // const releaseInfo = await restakingPoolContract.releaseInfoBetween(address, 0, releaseInfoLength);
        // console.log("releaseInfo", releaseInfo);
        // const ids = releaseInfo.map((item: any, index: number) => index);
        // logger.info("releaseInfo", { releaseInfoLength, releaseInfo, ids });
        const estimateGas = await restakingPoolContract.estimateGas.releaseWithLimit(releaseInfoLength);
        releasePrincipalTx = await restakingPoolContract.releaseWithLimit(releaseInfoLength, {
          gasLimit: estimateGas.add(ethers.utils.parseUnits("100000", "wei")),
        });
      } else {
        const estimateGas = await restakingPoolContract.estimateGas.release(ids);
        releasePrincipalTx = await restakingPoolContract.release(ids, {
          gasLimit: estimateGas.add(ethers.utils.parseUnits("100000", "wei")),
        });
      }
      const pendingTxnType = isAll ? "claim_all_principal" : "claim_principal";
      dispatch(
        fetchPendingTxns({
          txnHash: releasePrincipalTx.hash,
          text: "claim",
          type: pendingTxnType,
        }),
      );
      await releasePrincipalTx.wait();
    } catch (e) {
      const rpcError = e as IJsonRPCError;
      if (rpcError.code === -32603 && rpcError.message.indexOf("ds-math-sub-underflow") >= 0) {
        dispatch(
          error("You may be trying to stake more than your balance! Error code: 32603. Message: ds-math-sub-underflow"),
        );
      } else if ((e as any).code == "ACTION_REJECTED") {
        dispatch(error(t`User denied transaction signature.`));
        // dispatch(error((e as any).message));
      } else if (e == "cancel") {
        dispatch(error(t`User denied transaction signature.`));
      } else {
        // dispatch(error((e as any).message));
        dispatch(error((e as any).reason || (e as any).message || (e as any).data || (e as any)));
      }
      return;
    } finally {
      if (releasePrincipalTx) {
        // segmentUA(uaData);
        dispatch(clearPendingTxn(releasePrincipalTx.hash));
      }
    }
  },
);
